import type {
  AttackNode, AttackRoutine, Character, CombatModifierWithStates, DamageNode, ModifierEffect,
} from '../../../backend/shared/types/index';
import { BUILT_IN_BONUS_TYPES } from '../../../backend/shared/config/bonusTypes';
import { parseDiceExpr } from '@/engine/diceParser';
import { parseAttackModSource, resolveAttackModSource, resolveComponents, STAT_DEF_MAP } from '@/engine/statResolver';
import { appliedStackedEffects, combatModifierTargets } from './stackingResolver';

export interface CalculationLine {
  label: string;
  terms: Array<{ label: string; value: number; bonusType?: string }>;
  total: number;
}

const bonusTypes = Object.fromEntries(BUILT_IN_BONUS_TYPES.map((type) => [type.typeId, type]));
const bonusTypeNames = Object.fromEntries(BUILT_IN_BONUS_TYPES.map((type) => [type.typeId, type.name.toLowerCase()]));

function campaignTerms(
  modifiers: CombatModifierWithStates[],
  targets: Set<string>,
): CalculationLine['terms'] {
  const candidates = modifiers.flatMap((modifier) => modifier.effects
    .filter((effect): effect is ModifierEffect & { value: number; bonusTypeId: string } =>
      !effect.rollModifier && effect.value !== undefined && Boolean(effect.bonusTypeId) && targets.has(effect.targetAttribute))
    .map((effect) => ({ ...effect, modifierName: modifier.name })));

  return appliedStackedEffects(candidates, bonusTypes).map((effect) => ({
    label: effect.modifierName,
    value: effect.value,
    bonusType: bonusTypeNames[effect.bonusTypeId] ?? effect.bonusTypeId,
  }));
}

function attackTerms(attack: AttackNode, char: Character | null | undefined, gameSystem: AttackRoutine['gameSystem']) {
  if (attack.attackComponents?.length) {
    return resolveComponents(attack.attackComponents, char, 0, gameSystem).resolved;
  }
  if (attack.attackModSource) {
    const source = parseAttackModSource(attack.attackModSource, attack.attackMod);
    return [{
      label: source.type === 'stat_ref' ? (STAT_DEF_MAP[source.key]?.label ?? source.key) : 'base',
      value: resolveAttackModSource(source, char, attack.attackMod),
    }];
  }
  return [{ label: 'base', value: attack.attackMod }];
}

function damageTerms(node: DamageNode, char: Character | null | undefined, gameSystem: AttackRoutine['gameSystem']) {
  const embedded = parseDiceExpr(node.diceExpr)?.modifier ?? 0;
  if (node.modComponents?.length) {
    const resolved = resolveComponents(node.modComponents, char, embedded, gameSystem).resolved;
    return embedded === 0 ? resolved : [{ label: 'base', value: embedded }, ...resolved];
  }
  return embedded === 0 ? [] : [{ label: 'base', value: embedded }];
}

/** Build the exact pre-roll attack and flat-damage calculations used by rollEngine. */
export function buildModifierCalculations(
  routine: AttackRoutine,
  enabledModifiers: CombatModifierWithStates[],
  char?: Character | null,
): CalculationLine[] {
  const attacks = routine.definition.flatMap((node) =>
    node.type === 'attack' ? [node] : node.type === 'bonus_attack' ? [node.attack] : []);
  const qualify = attacks.length > 1;
  const lines: CalculationLine[] = [];

  for (const attack of attacks) {
    const attackCampaign = campaignTerms(enabledModifiers, combatModifierTargets('attack', attack.attackKind));
    const attackLineTerms = [...attackTerms(attack, char, routine.gameSystem), ...attackCampaign];
    lines.push({
      label: qualify ? `Attack — ${attack.label}` : 'Attack',
      terms: attackLineTerms,
      total: attackLineTerms.reduce((sum, term) => sum + term.value, 0),
    });

    const filledDamage = attack.damage.filter((node) => node.diceExpr.trim() || (node.modComponents?.length ?? 0) > 0);
    const damageCampaign = campaignTerms(enabledModifiers, combatModifierTargets('damage', attack.attackKind));
    filledDamage.forEach((node, index) => {
      const terms = [...damageTerms(node, char, routine.gameSystem), ...(index === 0 ? damageCampaign : [])];
      lines.push({
        label: qualify || filledDamage.length > 1
          ? `Damage — ${attack.label}${filledDamage.length > 1 ? ` (${node.notes || `row ${index + 1}`})` : ''}`
          : 'Damage',
        terms,
        total: terms.reduce((sum, term) => sum + term.value, 0),
      });
    });
  }

  return lines;
}

export function formatCalculationTerm(term: CalculationLine['terms'][number]): string {
  const signed = `${term.value >= 0 ? '+' : ''}${term.value}`;
  return `${signed} ${term.label}${term.bonusType ? ` (${term.bonusType})` : ''}`;
}
